import { Box, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDebounce } from "react-use";
import { LightningLanderThemeSettings } from "../types";
import { landerDefaultTheme } from "../providers/LightningLanderThemeProvider";

interface ILightningLanderThemeEditorProps {
  theme: LightningLanderThemeSettings;
  setTheme: (theme: LightningLanderThemeSettings) => void;
}

function LightningLanderThemeEditor({ theme, setTheme }: ILightningLanderThemeEditorProps) {
  const fullTheme: LightningLanderThemeSettings = {
    ...landerDefaultTheme,
    ...theme,
  };
  const [localTheme, setLocalTheme] = useState(fullTheme);
  useEffect(() => {
    setLocalTheme(fullTheme);
  }, [theme]);

  // Debounce updates to the global theme to avoid excessive rerenders
  useDebounce(
    () => {
      setTheme(localTheme);
    },
    400,
    [localTheme]
  );

  return (
    <Box p={2}>
      <Typography variant="h6" mb={2}>
        Theme Settings
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Primary Color"
          type="color"
          value={localTheme.primaryColor}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, primaryColor: e.target.value }))
          }
        />
        <TextField
          label="Secondary Color"
          type="color"
          value={localTheme.secondaryColor}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, secondaryColor: e.target.value }))
          }
        />
        <TextField
          label="Text Color"
          type="color"
          value={localTheme.textColor}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, textColor: e.target.value }))
          }
        />
        <TextField
          label="Font Family"
          value={localTheme.bodyFontFamily}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, bodyFontFamily: e.target.value }))
          }
        />
        <TextField
          label="H1 Font Size"
          value={localTheme.fontSizeH1}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH1: e.target.value }))
          }
        />
        <TextField
          label="H2 Font Size"
          value={localTheme.fontSizeH2}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH2: e.target.value }))
          }
        />
        <TextField
          label="H3 Font Size"
          value={localTheme.fontSizeH3}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH3: e.target.value }))
          }
        />
        <TextField
          label="H4 Font Size"
          value={localTheme.fontSizeH4}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH4: e.target.value }))
          }
        />
        <TextField
          label="H5 Font Size"
          value={localTheme.fontSizeH5}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH5: e.target.value }))
          }
        />
        <TextField
          label="H6 Font Size"
          value={localTheme.fontSizeH6}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeH6: e.target.value }))
          }
        />
        <TextField
          label="Body Font Size"
          value={localTheme.fontSizeBody}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, fontSizeBody: e.target.value }))
          }
        />
        <TextField
          label="Container max width"
          value={localTheme.containerWidth}
          onChange={(e) =>
            setLocalTheme((t) => ({ ...t, containerWidth: e.target.value }))
          }
          placeholder="e.g. 1200px"
          helperText="Max width for section containers (used by Section block)"
        />
      </Stack>
    </Box>
  );
}

export default LightningLanderThemeEditor;
